{#if config.isCourseTypeSelfPaced}
    <TwoColumnPage>
        <div slot="sidebar">
            <CourseInfoBox>
                <div class="itsl-course-info-box__progress-bar" slot="custom">
                    {#if viewProgressBarAndLinks}
                        <span class="itsl-course-info-box__progress-bar__title">{i18n.progress}</span>
                        <ProgressBar
                            accessibleText={i18n.completedXofYPlans}
                            displayText={i18n.completedXofYPlans}
                            max={config.numTotalPlans}
                            value={numbOfCompletedPlans}
                        />
                        {#if !isCourseCompleted}
                            <div class="itsl-course-info-box__progess-plan-link">
                                <Link
                                    href={destinationUrl}
                                    iconPlacement="end"
                                    kind="primary-standalone"
                                    text={linkText}
                                />
                            </div>
                        {/if}
                    {/if}
                </div>
            </CourseInfoBox>
        </div>
        <div slot="content">
            <Banner close={i18n.close} text={config.bannerText} type="{bannerType}" bind:visible={showBanner} />
            <HeadingRow
                {config}
                headerText={i18n.plansHeader}
                {i18n}
                {optionItems}
                {selectedAction}
                {showStopSharingPlannerConfirmModal}
                {stopSharingModalClicked}
            />
            <SelfPacedPlanTabs on:planCompletionUpdate={plansCompletionUpdate} on:tabChanged={updateSelectedTab} />
        </div>
    </TwoColumnPage>
{:else}
    <Banner close={i18n.close} text={config.bannerText} type="{bannerType}" bind:visible={showBanner} />
    <div class="itsl-plans_plan_page">
        <HeadingRow
            {config}
            {i18n}
            {optionItems}
            {selectedAction}
            {showStopSharingPlannerConfirmModal}
            {stopSharingModalClicked}
        />
        {#if !isSelfPacedCourse}
            <PlanTabs />
        {:else}
            <SelfPacedPlanTabs />
        {/if}
    </div>
{/if}

<script>
    import Banner from '@itslearning/prometheus/assets/feedback/Banner/v1/Banner.svelte';
    import Link from '@itslearning/prometheus/assets/navigation/Link/v1/Link.svelte';
    import TwoColumnPage from '@itslearning/prometheus/assets/templates/TwoColumnPage/v1/TwoColumnPage.svelte';

    import { onMount } from "svelte";
    import { stopSharingPlanner, completedPlans, getLastOpenedPlan, getCourseCompletionStatus } from "@its-ui/planner-common/api";
    import config from "@its-ui/planner-common/config";
    import { columnTypes, columnVisibility, onePlanAtATimePlanStatus, plannerTabIds, readyMadeContentImportStatus, PLAN_COMPLETION_UPDATE_EVENT, personCourseStatus } from "@its-ui/planner-common/constants";
    import i18n from "@its-ui/planner-common/i18n";
    import { loadAllPlans } from "@its-ui/planner-common/logic/plans/loadDataController";
    import { allPlansData } from "@its-ui/planner-common/logic/plans/stores";
    import { addBackDestinationParam } from "@its-ui/planner-common/helpers/backLinkHelper.js";
    import CourseInfoBox from "@its-ui/course-info-box/src/lib/App/App.svelte";
    import ProgressBar from "@its-ui/planner-common/components/ProgressBar/ProgressBar.svelte";

    import HeadingRow from "./HeadingRow.svelte";
    import PlanTabs from "../PlanTabs/PlanTabs.svelte";
    import SelfPacedPlanTabs from "../SelfPacedPlanTabs/SelfPacedPlanTabs.svelte";

    const isSelfPacedCourse =
        config.columnVisibilities[columnTypes.Date] === columnVisibility.Nobody;
    const canStopSharingPlanner = config.canSharePlanner && config.canUpdateSharingPlanner;
    const stopSiteSharingSelectValue = 1;

    let showStopSharingPlannerConfirmModal = false;
    let stopSharingPerformed = false;

    $: numbOfCompletedPlans = config.numCompletedPlans;
    $: showBanner = config.bannerText !== "";
    $: bannerType = config.readyMadeContentImportStatus === readyMadeContentImportStatus.fullyImported ? "info" : "warning";
    $: isCourseEmpty = config.numTotalPlans === 0;
    $: isCourseCompleted = config.numTotalPlans === numbOfCompletedPlans;
    $: viewProgressBarAndLinks = config.isCourseTypeSelfPaced && !config.canEdit && config.canViewProgressBar && !isCourseEmpty && config.isLearner;

    $: optionItems = getActions();

    // Actions in course information box
    let currentTab = plannerTabIds.all;// Keeps track of which tab is selected between "All plans" and "Topic"
    let backUrl;
    let linkText;
    let lastOpenedPlan;

    $: destinationUrl = addBackDestinationParam(backUrl, currentTab);

    $: if (stopSharingPerformed) {
        optionItems = getActions();
        stopSharingPerformed = false;
    }

    onMount(async () => {
        if (viewProgressBarAndLinks) {
            lastOpenedPlan = await getLastOpenedPlan();
            const personCourseCompletionStatusOnMount = await getCourseCompletionStatus();

            if (lastOpenedPlan && numbOfCompletedPlans > 0 && personCourseCompletionStatusOnMount === personCourseStatus.inProgress) { // Continue status
                backUrl = lastOpenedPlan.url;
                linkText = i18n.continue;
            }
            else { // The course is either completed, not started (personCourseStatus) or lastOpenedPlan == null (current plan is not in lessonpersonprogress)
                await loadAllPlans(config.publicAccessKey);
                if ($allPlansData.plans && $allPlansData.plans.length !== numbOfCompletedPlans) { // The course is not completed
                    if (numbOfCompletedPlans === 0) { // Gets the plan to start on, the first
                        backUrl = $allPlansData.plans[0].url;
                        linkText = i18n.start;
                    }
                    else {
                       backUrl = $allPlansData.plans.find(f => f.isCurrentPlan === true)?.url;
                       linkText = i18n.continue;
                    }
                }
            }
        }
    });

    function selectedAction(event) {
        switch (event.detail.data) {
            case stopSiteSharingSelectValue:
                showStopSharingPlannerConfirmModal = true;
                break;
        }
    }

    function getActions() {
        const options = [];

        options.push({
            icon: "",
            label: i18n.planSettings,
            link: true,
            url: config.settingPageUrl,
            target: "_self"
        });

        if (canStopSharingPlanner && !stopSharingPerformed) {
            options.push({
                icon: "",
                label: i18n.stopSiteSharing,
                link: false,
                target: "_self",
                value: stopSiteSharingSelectValue
            });
        }

        if (config.canSharePlanner) {
            options.push({
                icon: "",
                label:
                    config.canUpdateSharingPlanner && !stopSharingPerformed
                        ? i18n.updateSiteSharing
                        : i18n.shareWithSites,
                link: true,
                url: config.shareToLibraryPageUrl,
                target: "_self"
            });
        }

        options.push({
            icon: "",
            label: i18n.importExport,
            link: true,
            url: config.importExportPageUrl,
            target: "_self",
        });

        return [{ options: options }];
    }

    function stopSharingModalClicked(event) {
        if (event.detail.confirmed === true) {
            stopSharingPlanner(config.plannerShareableObjectId).then(() => {
                stopSharingPerformed = true;
            });
        }

        showStopSharingPlannerConfirmModal = false;
    }

    async function plansCompletionUpdate() {
        numbOfCompletedPlans = await completedPlans(config.courseId);
    }

    function updateSelectedTab({ detail }) {
        currentTab = detail.tab;
        destinationUrl = addBackDestinationParam(backUrl, currentTab);
    }
</script>
